import React, { useState } from 'react';
import { ArrowLeft, Bell, CheckCheck, RotateCcw, Truck, AlertTriangle, Sparkles, Settings, Package, ArrowUpRight, CheckCircle2, Lock } from 'lucide-react';
type NotificationCategory = 'resi' | 'corrieri' | 'anomalie' | 'spedizioni' | 'sistema';
export type NotificationTarget = {
  context: 'shipments' | 'carriers' | 'stores' | 'analytics' | 'account';
  activeItem: string;
  shipmentId?: string;
  anomalyId?: string;
  resoId?: string;
};
type NotificationItem = {
  id: string;
  category: NotificationCategory;
  title: string;
  description: string;
  timestamp: string;
  group: 'oggi' | 'ieri' | 'settimana';
  unread: boolean;
  actionLabel?: string;
  target?: NotificationTarget;
};
const NOTIFICATIONS: NotificationItem[] = [{
  id: 'n-001',
  category: 'resi',
  title: 'Nuova richiesta di reso da Milano Duomo',
  description: 'Sara Bianchi ha aperto una richiesta di reso per pantaloni grigi della collezione SS25 con cucitura difettosa.',
  timestamp: '12 min fa',
  group: 'oggi',
  unread: true,
  actionLabel: 'Apri richiesta',
  target: {
    context: 'returns',
    activeItem: 'Richieste',
    resoId: 'RES-2840-039'
  }
}, {
  id: 'n-002',
  category: 'corrieri',
  title: 'Sblocco corriere richiesto: UPS',
  description: 'Il vettore UPS richiede sblocco per riprendere ritiri programmati dopo verifica documentale.',
  timestamp: '28 min fa',
  group: 'oggi',
  unread: true,
  actionLabel: 'Verifica e sblocca',
  target: {
    context: 'carriers',
    activeItem: 'Performance'
  }
}, {
  id: 'n-003',
  category: 'anomalie',
  title: 'Anomalia urgente su SH-7H2K9P',
  description: 'Aisac ha rilevato uno slot di consegna in pausa pranzo: il negozio sarà chiuso al passaggio del vettore.',
  timestamp: '1 h fa',
  group: 'oggi',
  unread: true,
  actionLabel: 'Apri anomalia',
  target: {
    context: 'shipments',
    activeItem: 'Anomalie',
    anomalyId: 'ANM-2840-0218'
  }
}, {
  id: 'n-004',
  category: 'spedizioni',
  title: 'Spedizione consegnata: SH-2K6V4T',
  description: 'Consegna firmata correttamente presso Store Palermo La Torre. 7 colli ricevuti.',
  timestamp: '2 h fa',
  group: 'oggi',
  unread: false,
  actionLabel: 'Vedi spedizione',
  target: {
    context: 'shipments',
    activeItem: 'Tutte le spedizioni',
    shipmentId: 'SH-2K6V4T'
  }
}, {
  id: 'n-005',
  category: 'resi',
  title: 'Reso approvato: RES-2840-041',
  description: 'Il reso della giacca rosa di Firenze è stato approvato. Ritiro programmato per il 12 mag.',
  timestamp: '3 h fa',
  group: 'oggi',
  unread: false,
  actionLabel: 'Vedi reso',
  target: {
    context: 'returns',
    activeItem: 'In corso',
    resoId: 'RES-2840-041'
  }
}, {
  id: 'n-006',
  category: 'corrieri',
  title: 'Tariffe FedEx aggiornate',
  description: 'Le nuove tariffe per le tratte nazionali entrano in vigore dal 15 mag. Variazione media +2,4%.',
  timestamp: 'Ieri 18:22',
  group: 'ieri',
  unread: false,
  actionLabel: 'Vedi tariffe',
  target: {
    context: 'carriers',
    activeItem: 'Tariffe'
  }
}, {
  id: 'n-007',
  category: 'anomalie',
  title: 'Anomalia risolta automaticamente',
  description: 'Aisac ha corretto un CAP errato sulla spedizione SH-6B9C5Y, evitando un mancato recapito.',
  timestamp: 'Ieri 14:23',
  group: 'ieri',
  unread: false,
  actionLabel: 'Vedi anomalia',
  target: {
    context: 'shipments',
    activeItem: 'Anomalie'
  }
}, {
  id: 'n-008',
  category: 'sistema',
  title: 'Nuovo insight AI disponibile',
  description: 'Aisac suggerisce di rivedere le fasce di chiusura negozi per ridurre le giacenze.',
  timestamp: 'Ieri 09:10',
  group: 'ieri',
  unread: false,
  actionLabel: 'Vedi giacenze',
  target: {
    context: 'shipments',
    activeItem: 'Giacenze'
  }
}, {
  id: 'n-009',
  category: 'spedizioni',
  title: 'Ritardo segnalato su tratta Roma → Bari',
  description: 'Maltempo sulla A14: ETA aggiornata per 3 spedizioni FedEx in viaggio verso il sud Italia.',
  timestamp: '3 g fa',
  group: 'settimana',
  unread: false,
  actionLabel: 'Vedi spedizioni',
  target: {
    context: 'shipments',
    activeItem: 'Tutte le spedizioni'
  }
}, {
  id: 'n-010',
  category: 'corrieri',
  title: 'Contratto GLS in scadenza',
  description: 'Il contratto con GLS scade il 30 giugno. Necessario rinnovo o rinegoziazione tariffe.',
  timestamp: '4 g fa',
  group: 'settimana',
  unread: false,
  actionLabel: 'Apri contratto',
  target: {
    context: 'carriers',
    activeItem: 'Contratti'
  }
}];
const CATEGORY_META: Record<NotificationCategory, {
  label: string;
  Icon: any;
  color: string;
  bg: string;
  border: string;
}> = {
  resi: {
    label: 'Resi',
    Icon: RotateCcw,
    color: 'text-[#D4FF3A]',
    bg: 'bg-[#D4FF3A]/10',
    border: 'border-[#D4FF3A]/30'
  },
  corrieri: {
    label: 'Corrieri',
    Icon: Truck,
    color: 'text-[#FAFAFA]',
    bg: 'bg-[#1F1F1F]',
    border: 'border-[#2A2A2A]'
  },
  anomalie: {
    label: 'Anomalie',
    Icon: AlertTriangle,
    color: 'text-[#EF4444]',
    bg: 'bg-[#EF4444]/10',
    border: 'border-[#EF4444]/30'
  },
  spedizioni: {
    label: 'Spedizioni',
    Icon: Package,
    color: 'text-[#FAFAFA]',
    bg: 'bg-[#1F1F1F]',
    border: 'border-[#2A2A2A]'
  },
  sistema: {
    label: 'Sistema',
    Icon: Sparkles,
    color: 'text-[#D4FF3A]',
    bg: 'bg-[#D4FF3A]/10',
    border: 'border-[#D4FF3A]/30'
  }
};
const FILTERS: {
  key: 'tutte' | NotificationCategory;
  label: string;
}[] = [{
  key: 'tutte',
  label: 'Tutte'
}, {
  key: 'resi',
  label: 'Resi'
}, {
  key: 'corrieri',
  label: 'Corrieri'
}, {
  key: 'anomalie',
  label: 'Anomalie'
}, {
  key: 'spedizioni',
  label: 'Spedizioni'
}, {
  key: 'sistema',
  label: 'Sistema'
}];
const GROUP_LABELS: Record<'oggi' | 'ieri' | 'settimana', string> = {
  oggi: 'Oggi',
  ieri: 'Ieri',
  settimana: 'Questa settimana'
};
type Props = {
  onBack: () => void;
  onNavigate?: (target: NotificationTarget) => void;
};
export function Notifications({
  onBack,
  onNavigate
}: Props) {
  const [filter, setFilter] = useState<'tutte' | NotificationCategory>('tutte');
  const [items, setItems] = useState(NOTIFICATIONS);
  const filtered = items.filter(n => filter === 'tutte' || n.category === filter);
  const unreadCount = items.filter(n => n.unread).length;
  const groups: Array<'oggi' | 'ieri' | 'settimana'> = ['oggi', 'ieri', 'settimana'];
  const markAllRead = () => setItems(prev => prev.map(n => ({
    ...n,
    unread: false
  })));
  const markOneRead = (id: string) => setItems(prev => prev.map(n => n.id === id ? {
    ...n,
    unread: false
  } : n));
  return <div className="space-y-6" data-id="element-817">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-818">
        <ArrowLeft className="h-4 w-4" data-id="element-819" />
        <span data-id="element-820">Torna alla dashboard</span>
      </button>

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-821">
        <div data-id="element-822">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight flex items-center gap-3" data-id="element-823">
            Notifiche
            {unreadCount > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-[#D4FF3A] text-[#0A0A0A]" data-id="element-824">
                {unreadCount}
              </span>}
          </h1>
        </div>
        {unreadCount > 0 && <button onClick={markAllRead} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#9A9A9A] hover:text-[#FAFAFA] hover:border-[#333] text-sm font-medium transition" data-id="element-825">
            <CheckCheck className="h-4 w-4" data-id="element-826" />
            Segna tutte come lette
          </button>}
      </div>

      {/* Filters */}
      <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto" data-id="element-827">
        <div className="flex items-center gap-1 p-1 rounded-lg bg-[#181818] border border-[#262626] w-max" data-id="element-828">
          {FILTERS.map(f => {
          const count = f.key === 'tutte' ? items.length : items.filter(n => n.category === f.key).length;
          return <button key={f.key} onClick={() => setFilter(f.key)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${filter === f.key ? 'bg-[#0A0A0A] text-[#FAFAFA] border border-[#262626]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-829">
                {f.label}
                <span className={`font-mono text-[10px] ${filter === f.key ? 'text-[#D4FF3A]' : 'text-[#6B6B6B]'}`} data-id="element-830">
                  {count}
                </span>
              </button>;
        })}
        </div>
      </div>

      {/* Groups */}
      {filtered.length === 0 ? <div className="bg-[#181818] border border-[#262626] rounded-xl p-12 text-center" data-id="element-831">
          <Bell className="h-8 w-8 text-[#6B6B6B] mx-auto mb-3" data-id="element-832" />
          <p className="text-sm text-[#9A9A9A]" data-id="element-833">
            Nessuna notifica in questa categoria.
          </p>
        </div> : <div className="space-y-6" data-id="element-834">
          {groups.map(g => {
        const groupItems = filtered.filter(n => n.group === g);
        if (groupItems.length === 0) return null;
        return <section key={g} data-id="element-835">
                <h2 className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-3" data-id="element-836">
                  {GROUP_LABELS[g]}
                </h2>
                <div className="bg-[#181818] border border-[#262626] rounded-xl divide-y divide-[#1F1F1F] overflow-hidden" data-id="element-837">
                  {groupItems.map(n => <NotificationRow key={n.id} notification={n} onClick={() => markOneRead(n.id)} onAction={() => {
              markOneRead(n.id);
              if (n.target) onNavigate?.(n.target);
            }} data-id="element-838" />)}
                </div>
              </section>;
      })}
        </div>}
    </div>;
}
function NotificationRow({
  notification,
  onClick,
  onAction
}: {
  notification: NotificationItem;
  onClick: () => void;
  onAction: () => void;
}) {
  const meta = CATEGORY_META[notification.category];
  const {
    Icon
  } = meta;
  return <div role="button" tabIndex={0} onClick={() => {
    if (notification.target) onAction();else onClick();
  }} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (notification.target) onAction();else onClick();
    }
  }} className="p-4 sm:p-5 flex gap-3 sm:gap-4 hover:bg-[#0A0A0A] transition-colors cursor-pointer relative" data-id="element-839">
      <div className={`h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ${meta.bg} ${meta.border} ${meta.color}`} data-id="element-840">
        <Icon className="h-5 w-5" strokeWidth={1.75} data-id="element-841" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-842">
        <div className="flex items-start justify-between gap-3 flex-wrap" data-id="element-843">
          <div className="min-w-0" data-id="element-844">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap" data-id="element-845">
              <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-846">
                {meta.label}
              </span>
              <span className="text-[#262626]" data-id="element-847">•</span>
              <span className="text-[11px] text-[#6B6B6B] font-mono" data-id="element-848">
                {notification.timestamp}
              </span>
            </div>
            <h3 className={`text-sm font-semibold leading-snug ${notification.unread ? 'text-[#FAFAFA]' : 'text-[#9A9A9A]'}`} data-id="element-849">
              {notification.title}
            </h3>
            <p className="text-sm text-[#9A9A9A] mt-1 leading-relaxed" data-id="element-850">
              {notification.description}
            </p>
          </div>
        </div>
        {notification.actionLabel && <button onClick={e => {
        e.stopPropagation();
        onAction();
      }} className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-[#D4FF3A] hover:text-[#c2ed28] transition-colors" data-id="element-851">
            <span data-id="element-852">{notification.actionLabel}</span>
            <ArrowUpRight className="h-3 w-3" data-id="element-853" />
          </button>}
      </div>
    </div>;
}